import { type RefObject, useEffect, useState } from "react";

/**
 * Determines whether the floating desktop CTA button and the mobile sticky
 * bar should be visible, based on the user's scroll position relative to the
 * hero section, the closing-CTA section, and the footer.
 *
 * Both UI elements are hidden once the closing-CTA section or the footer
 * enters the viewport so they do not compete with the explicit CTA copy.
 */
export function useStickyCtaVisibility({
  heroRef,
}: {
  /** Ref attached to the hero section element. */
  heroRef: RefObject<HTMLElement | null>;
}): { showFixedButton: boolean; showMobileBar: boolean } {
  const [showFixedButton, setShowFixedButton] = useState(true);
  const [showMobileBar, setShowMobileBar] = useState(false);

  /** Returns true when the closing-CTA section or footer has entered the viewport. */
  function isEndOfPageVisible(): boolean {
    const footer = document.querySelector("footer");
    const closingCta = document.getElementById("closing-cta");
    const isFooterVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
    const isClosingCtaVisible = closingCta ? closingCta.getBoundingClientRect().top < window.innerHeight : false;
    return isFooterVisible || isClosingCtaVisible;
  }

  // Hide fixed desktop button when the closing CTA or footer is visible.
  useEffect(() => {
    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setShowFixedButton(!isEndOfPageVisible());
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Show mobile sticky bar after hero leaves viewport; hide it near the page end.
  useEffect(() => {
    const heroEl = heroRef.current;
    if (!heroEl) {
      return;
    }

    // IntersectionObserver handles the initial hero-exit event.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting) {
          setShowMobileBar(!isEndOfPageVisible());
        } else {
          setShowMobileBar(false);
        }
      },
      { threshold: 0 },
    );

    observer.observe(heroEl);

    // Scroll handler keeps the mobile bar hidden when the end of page appears.
    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const heroRect = heroEl.getBoundingClientRect();
        const heroPastView = heroRect.bottom < 0;
        if (heroPastView) {
          setShowMobileBar(!isEndOfPageVisible());
        } else {
          setShowMobileBar(false);
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [heroRef]);

  return { showFixedButton: showFixedButton, showMobileBar: showMobileBar };
}
